import { useResizeObserver } from "@hooks";
import { Search as SearchIcon } from "@mui/icons-material";
import { Alert, AlertTitle, Button, Stack, Typography } from "@mui/material";
import {
  PtPatientOut,
  useLazyIguanaApiV1SearchPatientsQuery,
} from "@providers/hop-ord-server/api";
import { toLocaleDateTimeString } from "@utils";
import { useCallback, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { AutocompleteElement, TextFieldElement } from "react-hook-form-mui";
import { SiteOption } from "../types";
import ListBox from "./ListBox";
import ListItem from "./ListItem";

const LIST_BOX_MAX_HEIGHT = 180;

interface SelectPatientContentProps {
  clinics: SiteOption[];
  handleSelectPatient: (patient: PtPatientOut | undefined) => void;
  selectedPatientId: string | undefined;
  patients: PtPatientOut[];
  setPatients: (patients: PtPatientOut[]) => void;
  form: UseFormReturn<{
    clinic: SiteOption | null;
    searchPatient: string;
    selectedCourses: {
      [key: string]: boolean;
    };
  }>;
}

const SelectPatientContent = ({
  clinics,
  handleSelectPatient,
  selectedPatientId,
  patients,
  setPatients,
  form,
}: SelectPatientContentProps) => {
  const [
    searchPatients,
    { isFetching: isPatientFetching, isError: isPatientError },
  ] = useLazyIguanaApiV1SearchPatientsQuery();
  const [showNoResultsMsg, setShowNoResultsMsg] = useState(false);

  const handleSearchPatients = async () => {
    setPatients([]);
    handleSelectPatient(undefined);
    const selectedClinic = form.getValues("clinic");
    const searchPatient = form.getValues("searchPatient");
    if (!selectedClinic || !searchPatient) {
      return;
    }
    const data = await searchPatients({
      searchTerm: searchPatient,
      treatingDepartment: selectedClinic.value,
      tenantId: selectedClinic.tenantId,
    }).unwrap();

    setPatients(data);

    const validPatients = data.filter((patient) => !!patient.dateOfBirth);

    if (validPatients.length === 1) {
      handleSelectPatient(validPatients[0]);
    }
    if (validPatients.length === 0) {
      setShowNoResultsMsg(true);
    }
  };

  const handleSearchPatientChange = useCallback(() => {
    setShowNoResultsMsg(false);
  }, []);

  const clinic = form.watch("clinic", null);
  const searchPatient = form.watch("searchPatient", "");

  // this is just so the modal doesn't shrink when the results change
  const [latchedHeight, setLatchedHeight] = useState<number | "auto">("auto");
  const { ref: listBoxRef } = useResizeObserver(
    useCallback((size) => {
      setLatchedHeight((prev) => {
        if (prev === "auto" && size.height === 0) {
          return "auto";
        }
        const max = Math.max(prev === "auto" ? 0 : prev, size.height);
        return max;
      });
    }, []),
  );

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography>Select treating clinic and enter a patient ID:</Typography>
      <AutocompleteElement<SiteOption | null>
        label="Treating clinic"
        name="clinic"
        options={clinics}
        required
        autocompleteProps={{
          isOptionEqualToValue: (option, value) =>
            !!option && !!value && option.label === value.label,
          getOptionLabel: (option) => (option ? option.label : ""),
        }}
      />
      <Stack
        direction="row"
        sx={{
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextFieldElement
          label="Patient ID"
          name="searchPatient"
          required
          onChange={handleSearchPatientChange}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleSearchPatients}
          disabled={!clinic || !searchPatient}
          startIcon={<SearchIcon />}
          size="large"
        >
          Search
        </Button>
      </Stack>
      {isPatientError ? (
        <Alert
          severity="error"
          sx={{ backgroundColor: "error.light", color: "error.dark" }}
        >
          <AlertTitle>Connection Error</AlertTitle>
          Please try again or create a Careplan manually
        </Alert>
      ) : (
        <Stack
          ref={listBoxRef}
          sx={{
            minHeight: latchedHeight,
            maxHeight: LIST_BOX_MAX_HEIGHT,
          }}
        >
          <ListBox
            title="Patients"
            isLoading={isPatientFetching}
            noResults={patients && patients.length === 0}
            noResultsMessage={
              showNoResultsMsg ? "No patients found" : undefined
            }
          >
            {patients.map((patient, idx) => {
              const dobString = patient.dateOfBirth
                ? toLocaleDateTimeString(patient.dateOfBirth, "date")
                : null;
              return (
                <ListItem
                  key={`${patient.patientId}-${idx}`}
                  title={`${patient.firstName} ${patient.lastName}`}
                  subtitle={`Patient ID: ${patient.patientId} | DOB: ${dobString ?? "-"}`}
                  checked={selectedPatientId === patient.patientId}
                  variant="radio"
                  onChange={() => handleSelectPatient(patient)}
                  disabled={dobString === null}
                />
              );
            })}
          </ListBox>
        </Stack>
      )}
    </Stack>
  );
};

export default SelectPatientContent;
